import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Truck, Briefcase, Megaphone, ChartLineUp, CheckCircle, Sparkle } from "@phosphor-icons/react";

export default function FranchiseSection() {
  const [f, setF] = useState({
    contact_name: "", contact_email: "", contact_phone: "", company_name: "",
    proposed_region: "", expected_customers: "",
    has_experience: false,
    needs_vehicle: true, needs_equipment: true, needs_marketing: true,
    investment_budget_eur: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/franchise/apply", {
        ...f,
        expected_customers: f.expected_customers ? parseInt(f.expected_customers) : null,
        investment_budget_eur: f.investment_budget_eur ? parseInt(f.investment_budget_eur) : null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section id="franchise" className="py-24" style={{ background: "linear-gradient(180deg, #163341 0%, #0e1a23 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 text-white">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: "#3FA64E" }}>05 — Franchise</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-3 leading-[1.05] tracking-tight">
              Bauen Sie<br />Ihr eigenes Tonneva auf.
            </h2>
            <p className="text-white/70 mt-5 text-lg max-w-md">
              Sie wollen in Ihrer Region Mülltonnenreinigung anbieten — aber Fahrzeug, Equipment und Software fehlen?
              Mit dem Tonneva-Franchise starten Sie ohne Kapitalbarriere. Wir organisieren alles.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                { i: <Truck weight="bold" />, t: "Fahrzeug & Anhänger", d: "Reinigungsfahrzeug, geleast oder finanziert über Tonneva" },
                { i: <Briefcase weight="bold" />, t: "Equipment & Tech", d: "Hochdruckreinigung, Wassertank, Tonneva-Backoffice-Lizenz" },
                { i: <Megaphone weight="bold" />, t: "Marketing & Launch", d: "Lokales SEO, Flyer, Social Media — wir holen Ihre ersten 100 Kunden" },
                { i: <ChartLineUp weight="bold" />, t: "Coaching & Support", d: "Onboarding-Schulung, monatlicher Business-Review, 24/7 Tech-Hotline" },
              ].map((p) => (
                <li key={p.t} className="flex gap-3">
                  <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: "rgba(63,166,78,0.15)", color: "#3FA64E" }}>{p.i}</div>
                  <div>
                    <div className="font-semibold">{p.t}</div>
                    <div className="text-sm text-white/60">{p.d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 p-4 rounded-xl border border-white/10 inline-flex items-center gap-3" style={{ background: "rgba(63,166,78,0.05)" }}>
              <Sparkle size={20} weight="fill" style={{ color: "#3FA64E" }} />
              <div className="text-sm">
                <div className="font-semibold">Aktuell freie Regionen</div>
                <div className="text-white/60">DACH-weit, Schwerpunkt 2026: BW, Bayern, NRW</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            {submitted ? (
              <div className="rounded-2xl p-10 text-center" style={{ background: "rgba(63,166,78,0.1)", border: "1px solid rgba(63,166,78,0.3)" }} data-testid="franchise-success">
                <CheckCircle size={56} weight="bold" className="mx-auto mb-4" style={{ color: "#3FA64E" }} />
                <h3 className="font-heading text-2xl font-bold mb-2">Bewerbung eingegangen!</h3>
                <p className="text-white/70">Wir melden uns innerhalb von 48 Stunden bei Ihnen.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl p-8 bg-white text-zinc-900" data-testid="franchise-form">
                <h3 className="font-heading text-2xl font-black tracking-tight mb-2">Jetzt bewerben</h3>
                <p className="text-sm text-zinc-500 mb-6">Unverbindlich. Dauer ca. 2 Minuten.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Vor- und Nachname *</Label><Input className="h-11 rounded-md" required value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="fra-name" /></div>
                  <div><Label className="text-xs">Firma (optional)</Label><Input className="h-11 rounded-md" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="fra-company" /></div>
                  <div><Label className="text-xs">E-Mail *</Label><Input type="email" required className="h-11 rounded-md" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="fra-email" /></div>
                  <div><Label className="text-xs">Telefon</Label><Input className="h-11 rounded-md" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} data-testid="fra-phone" /></div>
                  <div className="col-span-2"><Label className="text-xs">Gewünschte Region *</Label><Input className="h-11 rounded-md" required placeholder="z. B. München, PLZ 79xxx" value={f.proposed_region} onChange={(e) => setF({ ...f, proposed_region: e.target.value })} data-testid="fra-region" /></div>
                  <div><Label className="text-xs">Erwartete Kunden</Label><Input type="number" className="h-11 rounded-md" placeholder="100" value={f.expected_customers} onChange={(e) => setF({ ...f, expected_customers: e.target.value })} data-testid="fra-cust" /></div>
                  <div><Label className="text-xs">Eigenkapital (€)</Label><Input type="number" className="h-11 rounded-md" placeholder="10000" value={f.investment_budget_eur} onChange={(e) => setF({ ...f, investment_budget_eur: e.target.value })} data-testid="fra-budget" /></div>
                  <div className="col-span-2 mt-2 space-y-2">
                    <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Wo brauchen Sie Unterstützung?</Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { k: "needs_vehicle", l: "Fahrzeug" },
                        { k: "needs_equipment", l: "Equipment" },
                        { k: "needs_marketing", l: "Marketing" },
                        { k: "has_experience", l: "Habe Erfahrung" },
                      ].map((o) => (
                        <label key={o.k} className={`flex items-center gap-2 px-3 py-2 rounded-full border-2 cursor-pointer text-xs font-medium transition ${f[o.k] ? "" : "border-zinc-200 text-zinc-600"}`} style={f[o.k] ? { borderColor: "#3FA64E", background: "rgba(63,166,78,0.06)", color: "#1d6781" } : {}}>
                          <Checkbox checked={f[o.k]} onCheckedChange={(v) => setF({ ...f, [o.k]: !!v })} className="rounded" data-testid={`fra-${o.k}`} />
                          {o.l}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="col-span-2"><Label className="text-xs">Nachricht (optional)</Label><Textarea className="rounded-md min-h-24" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Erzählen Sie uns kurz von sich…" data-testid="fra-message" /></div>
                </div>
                {error && <div className="mt-4 p-3 rounded-md bg-red-50 text-red-700 text-sm" data-testid="fra-error">{error}</div>}
                <Button type="submit" disabled={busy} className="w-full mt-6 hover:opacity-95 text-white rounded-full h-12 shadow-lg" style={{ background: "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)" }} data-testid="fra-submit">
                  {busy ? "Wird gesendet…" : "Bewerbung absenden"}
                </Button>
                <p className="text-xs text-zinc-400 text-center mt-3">DSGVO-konform · keine Weitergabe an Dritte</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
